/**
 * Pure logic helpers for TemplateBlocksSection. Extracted so they can
 * be unit-tested without mounting a full React tree (no DnDContext / no
 * tRPC mocks). The container imports them from here.
 */
import type {
  CadenceGraph,
  CadenceGraphNode,
  SvrCadenceTemplateStep,
} from '@poseidon/types/svr/svrCadenceTemplate.type';
import type { CadenceStepTypePure } from '@poseidon/components/CGStep';
import type { StepData } from '@poseidon/components/CGStep/CTSContentEditingPure';
import type { RouterInputs } from '@poseidon/api/types';
import type CadenceStepOption from '@poseidon/types/pg-gen/cadenceStepOption';

import type { DelayBlockInfo } from '~/components/CadenceTemplateSteps3/extractDelayBlocks';

type AddStepParams = RouterInputs['cadence']['createStep']['data'];

/**
 * For each delay block, return its "last runnable step id" — the
 * `afterStepId` to pass to `cadenceTemplate.createStepV2` when
 * inserting a new delay block immediately after this one.
 *
 * - Filters `block.group` to non-delay / non-wait_for_connect steps.
 * - Picks the LAST runnable in the group (insertion order).
 * - Falls back to the block id itself when the block has no runnable
 *   children (a freshly-created delay or a wait_for_connect, which
 *   has its children under `next.one` / `next.two`, not `group`).
 */
export function computeLastStepIdByBlock(
  blocks: readonly DelayBlockInfo[],
  graph: CadenceGraph<SvrCadenceTemplateStep>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const block of blocks) {
    const runnableIds = block.group.filter((id) => {
      const node = graph.nodes[id];
      return node != null && node.type !== 'delay' && node.type !== 'wait_for_connect';
    });
    out[block.id] = runnableIds[runnableIds.length - 1] ?? block.id;
  }
  return out;
}

/**
 * Step types currently in the template — used to disable once-only
 * types (connect / follow / like / sift) in the new-day picker. We
 * filter delay / wait_for_connect since those aren't user-pickable
 * action types.
 */
export function computeExistingStepTypes(
  graph: CadenceGraph<SvrCadenceTemplateStep>,
): readonly CadenceStepTypePure[] {
  return Object.values(graph.nodes)
    .map((n) => n.type)
    .filter((t): t is CadenceStepTypePure => t !== 'delay' && t !== 'wait_for_connect');
}

/**
 * Walk the `next.one` chain starting at `startId`, collecting every
 * delay / wait_for_connect block reachable along the way.
 *
 * Mirrors `extractDelayBlocks` but takes a custom start instead of
 * `graph.startId`. Used to extract a wait_for_connect's No-branch
 * chain (`next.two` start), which `extractDelayBlocks` skips because
 * it only walks the main `next.one` timeline.
 *
 * **Bare-leaf handling**: when `startId` points to a runnable node
 * (not a delay/wait_for_connect), we synthesize a "leading leaf"
 * block containing the leaf chain until we hit a delay. This is the
 * common case for No-branch steps — when the user adds the first
 * step under a wait_for_connect with `option='two'`, the server
 * creates a bare leaf (parent=wfc, no delay wrapper). Without this
 * handling, those leaves would be invisible in the UI even though
 * they exist in the database.
 *
 * The synthetic block uses `parentId` as its `id` so the host can
 * still target it with `afterStepId` for further inserts. `delay=0`
 * and `nextOne=null` since it's a leaf wrapper, not a real delay.
 *
 * Returns an empty array for null / missing startIds so callers can
 * pass `node.next.two ?? null` without a guard.
 */
export function walkChainFrom(
  graph: CadenceGraph<SvrCadenceTemplateStep>,
  startId: string | null | undefined,
  /**
   * When provided, the synthetic "leading leaf" block (created when
   * the chain starts with a runnable instead of a delay) uses this
   * ID. Defaults to the first leaf's id. Hosts pass the
   * `wait_for_connect` node id here so the synthetic block's
   * `afterStepId` resolves correctly for in-block inserts.
   */
  syntheticBlockId?: string,
): DelayBlockInfo[] {
  const blocks: DelayBlockInfo[] = [];
  let currentId: string | undefined = startId ?? undefined;
  if (currentId == null) return blocks;

  // Bare-leaf chain prefix: collect runnables until we hit a delay /
  // wait_for_connect / end. They form one synthetic "first day".
  const leadingLeafIds: string[] = [];
  while (currentId != null) {
    const node: CadenceGraphNode<SvrCadenceTemplateStep> | undefined =
      graph.nodes[currentId];
    if (node == null) break;
    if (node.type === 'delay' || node.type === 'wait_for_connect') break;
    leadingLeafIds.push(node.id);
    currentId = node.next.one ?? undefined;
  }
  if (leadingLeafIds.length > 0) {
    blocks.push({
      id: syntheticBlockId ?? leadingLeafIds[0]!,
      delay: 0,
      group: leadingLeafIds,
      nextOne: currentId ?? null,
    });
  }

  // Continue walking delay / wait_for_connect blocks.
  while (currentId != null) {
    const node: CadenceGraphNode<SvrCadenceTemplateStep> | undefined =
      graph.nodes[currentId];
    if (node == null) break;
    if (node.type === 'delay' || node.type === 'wait_for_connect') {
      blocks.push({
        id: node.id,
        delay: node.delay,
        alignWeek: node.delayAlignWeek,
        group: node.group,
        nextOne: node.next.one ?? null,
      });
      currentId = node.next.one ?? undefined;
    } else {
      // Non-delay node mid-chain — defensive: skip past it.
      currentId = node.next.one ?? undefined;
    }
  }
  return blocks;
}

/**
 * For a given block, derive a short human label describing the next
 * runnable step in the Yes branch — surfaces in the
 * `CampaignWaitForConnectPure` Yes-row preview. When the wait_for_connect
 * has no `next.one`, returns `undefined` so the renderer falls through
 * to its "End of cadence" placeholder.
 */
export function computeYesPreview(
  graph: CadenceGraph<SvrCadenceTemplateStep>,
  waitForConnectNodeId: string,
): { type: CadenceStepTypePure; label: string } | undefined {
  const wfc = graph.nodes[waitForConnectNodeId];
  const yesStartId = wfc?.next.one;
  if (yesStartId == null) return undefined;

  // The Yes branch usually starts at a delay block whose group has the
  // first runnable step. Walk forward until we find a runnable step we
  // can describe.
  let cursor: string | undefined = yesStartId;
  while (cursor != null) {
    const node: CadenceGraphNode<SvrCadenceTemplateStep> | undefined = graph.nodes[cursor];
    if (node == null) return undefined;
    if (node.type !== 'delay' && node.type !== 'wait_for_connect') {
      return { type: node.type as CadenceStepTypePure, label: stepLabel(node.type) };
    }
    // Inspect the delay block's group for a runnable step.
    const runnable = node.group
      .map((id: string) => graph.nodes[id])
      .find(
        (n: CadenceGraphNode<SvrCadenceTemplateStep> | undefined) =>
          n != null && n.type !== 'delay' && n.type !== 'wait_for_connect',
      );
    if (runnable != null) {
      return {
        type: runnable.type as CadenceStepTypePure,
        label: stepLabel(runnable.type),
      };
    }
    cursor = node.next.one;
  }
  return undefined;
}

function stepLabel(type: SvrCadenceTemplateStep['type']): string {
  switch (type) {
    case 'message':
      return 'Send a LinkedIn message';
    case 'inmail':
      return 'Send an InMail';
    case 'email':
      return 'Send an email';
    case 'connect':
      return 'Send a connection request';
    case 'follow':
      return 'Follow the profile';
    case 'visit':
      return 'Visit the profile';
    case 'likeRecentPosts':
      return 'Like recent posts';
    case 'reminder':
      return 'Manual reminder';
    case 'sift':
      return 'Filter contacts';
    case 'call':
      return 'Call contact';
    case 'voice_note':
      return 'Send a voice note';
    case 'delay':
      return 'Wait';
    case 'wait_for_connect':
      return 'Wait for connection';
    default:
      return type;
  }
}

/**
 * Decide what to do when the user picks a step type from the new-day /
 * in-block / no-branch picker.
 *
 *   - "Fast create" types (visit / follow / likeRecentPosts) require
 *     no body/subject/sift authoring, so we create them immediately
 *     with type-specific defaults. CRITICAL: `likeRecentPosts` MUST
 *     pass `postsToLike` (the schema makes it required, not optional);
 *     missing it makes the server throw a zod validation error. This
 *     was the source of the "adding like step errors out" bug.
 *
 *   - "Open editor" types (connect / message / inmail / email /
 *     reminder / sift / call / voice_note) need user-authored content
 *     before the server will accept the create. The host swaps in the
 *     inline `CTSContentEditingPure` editor; on submit the host calls
 *     `buildAddParamsFromEditor` to assemble the final create payload.
 *
 *   - `connect` is in the open-editor bucket because the campaign-side
 *     flow lets the user write the optional invite message inline. The
 *     resulting body2 is allowed to be empty by the server schema.
 */
export type StepCreatePlan =
  | { kind: 'fast-create'; defaults: Partial<AddStepParams> }
  | { kind: 'open-editor' };

export function planStepCreate(type: CadenceStepTypePure): StepCreatePlan {
  switch (type) {
    case 'visit':
    case 'follow':
      return { kind: 'fast-create', defaults: {} };
    case 'likeRecentPosts':
      // Server schema: `postsToLike: z.number()` (required).
      return { kind: 'fast-create', defaults: { postsToLike: 1 } };
    case 'connect':
    case 'message':
    case 'inmail':
    case 'email':
    case 'reminder':
    case 'sift':
    case 'call':
    case 'voice_note':
      return { kind: 'open-editor' };
    default:
      // Defensive: any future type defaults to opening the editor so
      // we never silently fast-create something the server might reject.
      return { kind: 'open-editor' };
  }
}

/**
 * Assemble an `AddStepParams` payload from a step type + the editor's
 * StepData output + per-context options (delay, afterStepId, option).
 *
 * Every server-required field for the type MUST land on the payload —
 * empty strings are real saves (the editor's empty-body guard runs
 * before this), so we forward every defined field. Fields that the
 * editor never produced (e.g. `cc` / `bcc` for a message step) stay
 * undefined and the server schema treats them as omitted.
 */
export function buildAddParamsFromEditor(
  type: CadenceStepTypePure,
  data: StepData,
  opts: {
    delay: number;
    /** Calendar-week aligned delay block (touch campaigns default new days to weeks). */
    delayAlignWeek?: boolean;
    afterStepId?: string;
    /**
     * Insert BEFORE this target step. Used for "add new day at top"
     * and "add new day between existing days". Server re-parents the
     * target under the new delay block. Mutually exclusive with
     * `afterStepId` — server prefers `beforeStepId` when both set.
     */
    beforeStepId?: string;
    option?: CadenceStepOption;
  },
): AddStepParams {
  return {
    type,
    delay: opts.delay,
    delayAlignWeek: opts.delayAlignWeek,
    afterStepId: opts.afterStepId,
    beforeStepId: opts.beforeStepId,
    option: opts.option,
    body2: data.body ?? undefined,
    subject: data.subject ?? undefined,
    postsToLike: data.postsToLike ?? undefined,
    cc: data.cc ?? undefined,
    bcc: data.bcc ?? undefined,
    onlyOpenLink: data.onlyOpenLink ?? undefined,
    siftConditions: (data.siftConditions ?? undefined) as
      | AddStepParams['siftConditions']
      | undefined,
    isPersonalized: data.isPersonalized ?? undefined,
    personalizationPrompt: data.personalizationPrompt ?? undefined,
    // Email steps only: continue the previous email's thread (reuses its subject). Absent ⇒ server
    // default (true). The editor only emits this when an earlier email exists.
    replyToPrevious: data.replyToPrevious ?? undefined,
  } as AddStepParams;
}

/**
 * Compute the subset of blocks that are SAFE to drag-reorder.
 *
 * Drag-reorder only permutes `delay` blocks. wait_for_connect blocks
 * themselves aren't moved — they're structurally tied to a connect
 * step. But blocks before AND after a wfc CAN be reordered as long
 * as the server's `cadenceTemplateReorderDelayGroups` re-links the
 * chain continuation (which it now does — the wfc keeps its parent
 * relationship correctly across reorders).
 *
 * Returns: every delay block in the chain (filtering out wfc).
 */
export function computeSortableBlocks(
  blocks: readonly DelayBlockInfo[],
  graph: CadenceGraph<SvrCadenceTemplateStep> | null,
): DelayBlockInfo[] {
  if (graph == null) return [];
  return blocks.filter((b) => graph.nodes[b.id]?.type === 'delay');
}

/**
 * Collect every step id that should be deleted when the user deletes
 * a day block. Returns ids in the order they should be deleted (leaves
 * first, then the connect day's runnable group last) so each delete's
 * triggers see the previous deletes' effects.
 *
 * For a regular delay block: just returns `block.group` (the runnable
 * children). The DB's after-delete trigger then bumps
 * `group_children_changed_on` on the parent delay, the before-update
 * trigger sees runnable_count=0, and the empty delay auto-deletes.
 *
 * For a connect day (a delay with a wait_for_connect chained as
 * next.one): also collects every step in the wfc's No-branch subtree
 * (parent=wfc, option='two', and everything chained from those).
 * Without this, the server's connect-delete throws "Wait for connect
 * step still has steps" when the user has any No-branch content. The
 * wfc itself doesn't need to be in the list — server auto-deletes it
 * via `cadenceTemplateDeleteWaitForConnection` when the connect step
 * deletes.
 */
export function collectStepsToDeleteForBlock(
  block: DelayBlockInfo,
  graph: CadenceGraph<SvrCadenceTemplateStep> | null,
): string[] {
  // We only collect RUNNABLE step ids — the surrounding delay /
  // wait_for_connect nodes are auto-deleted by DB triggers once their
  // last runnable child goes (after-delete bumps
  // group_children_changed_on, before-update sees runnable_count=0
  // and deletes the empty delay). Including delays in our delete
  // list would race against the auto-cleanup and risk
  // "Can't delete group that has steps" errors.
  const idsToDelete: string[] = [];

  if (graph != null) {
    const node = graph.nodes[block.id];
    const wfcChild = node?.next.one;
    const wfcNode = wfcChild != null ? graph.nodes[wfcChild] : undefined;
    if (wfcNode?.type === 'wait_for_connect') {
      // Walk the No-branch subtree and collect every runnable step
      // (parent=wfc → option='two' delay → group). DFS via stack;
      // push runnables to the result in encounter order so leaves of
      // earlier delays land before leaves of later delays. Doesn't
      // matter strictly for correctness (each runnable's delete is
      // independent), but keeps the order intuitive.
      const stack: string[] = wfcNode.next.two != null ? [wfcNode.next.two] : [];
      while (stack.length > 0) {
        const id = stack.pop()!;
        const n = graph.nodes[id];
        if (n == null) continue;
        for (const childId of n.group) idsToDelete.push(childId);
        for (const nextId of Object.values(n.next)) {
          if (nextId != null) stack.push(nextId);
        }
      }
    }
  }

  // Connect day's own runnables. Order matters here: the No-branch
  // runnables go FIRST so the wfc has no option-2 children when
  // server's connect-delete tries to auto-delete the wfc. Without
  // this the connect-delete throws "Wait for connect step still
  // has steps".
  idsToDelete.push(...block.group);

  return idsToDelete;
}

/**
 * Apply the server's `cadenceTemplateReorderDelayGroups` semantics to
 * a list of steps, returning a new array with parent_id rewritten:
 *
 * 1. For each id in orderedDelayIds[i]:
 *      step.parent_id = orderedDelayIds[i - 1] ?? null
 *
 * 2. CHAIN RE-LINK: any non-in-list delay/wait_for_connect whose
 *    parent was an in-list item gets re-parented to the NEW TAIL of
 *    the in-list. Without this, that continuation step keeps its old
 *    parent (which may now sit mid-chain after reorder) and the
 *    graph-build's last-write-wins makes one of two siblings invisible.
 *    This was the user-reported "wfc disappears after drag" bug.
 *
 * Steps NOT in `orderedDelayIds` AND not chain continuations
 * (runnables, leaves, etc.) keep their existing parent_id.
 *
 * Used by the optimistic-update path in
 * `useCadenceTemplateReorderStepsMutation` — extracted as a pure
 * function so the cache-patch logic is unit-testable without
 * mounting React or mocking React Query.
 */
export function applyOptimisticReorder<
  S extends { id: string; parent_id: string | null; type?: string },
>(steps: readonly S[], orderedDelayIds: readonly string[]): S[] {
  if (orderedDelayIds.length === 0) return [...steps];

  const orderedSet = new Set(orderedDelayIds);
  const newParentByStepId = new Map<string, string | null>();
  for (let i = 0; i < orderedDelayIds.length; i++) {
    newParentByStepId.set(orderedDelayIds[i]!, orderedDelayIds[i - 1] ?? null);
  }

  // Find chain continuations BEFORE rewriting (snapshot original
  // parents). A continuation = non-in-list delay/wait_for_connect
  // whose original parent is an in-list item.
  const newTailId = orderedDelayIds[orderedDelayIds.length - 1]!;
  for (const s of steps) {
    if (orderedSet.has(s.id)) continue;
    if (s.parent_id == null) continue;
    if (!orderedSet.has(s.parent_id)) continue;
    if (s.type !== 'delay' && s.type !== 'wait_for_connect') continue;
    // This is a chain continuation — re-parent to new tail.
    newParentByStepId.set(s.id, newTailId);
  }

  return steps.map((s) =>
    newParentByStepId.has(s.id)
      ? ({ ...s, parent_id: newParentByStepId.get(s.id) ?? null } as S)
      : s,
  );
}

/**
 * Convenience: build the fast-create payload for a step type that the
 * picker handled with `kind: 'fast-create'`. Composes the type-specific
 * defaults with the per-context options. Throws (via TypeScript exhaustion)
 * if called with an open-editor type — host should branch on
 * `planStepCreate` first.
 */
export function buildFastCreateParams(
  type: CadenceStepTypePure,
  opts: {
    delay: number;
    /** Calendar-week aligned delay block (touch campaigns default new days to weeks). */
    delayAlignWeek?: boolean;
    afterStepId?: string;
    beforeStepId?: string;
    option?: CadenceStepOption;
  },
): AddStepParams {
  const plan = planStepCreate(type);
  if (plan.kind !== 'fast-create') {
    throw new Error(`buildFastCreateParams called with non-fast-create type "${type}"`);
  }
  return {
    type,
    delay: opts.delay,
    delayAlignWeek: opts.delayAlignWeek,
    afterStepId: opts.afterStepId,
    beforeStepId: opts.beforeStepId,
    option: opts.option,
    ...plan.defaults,
  } as AddStepParams;
}
